import axios, { type AxiosError } from 'axios';
import { i18n } from '@/i18n';
import { notify } from '@/core/toast/notify';
import { isSocialReconnectRequired } from '@/modules/social/shared/socialConnectionErrors';
import type { ApiResponse } from '@/types/api';
import { consumePermissionDeniedHandled } from '@/utils/moduleNavigation';
import { shouldSuppressAuthError } from '@/utils/authTransition';

const CODE_HINT_KEYS: Record<string, string> = {
  PLAN_LIMIT_EXCEEDED: 'errors.planLimitExceeded',
  SUBSCRIPTION_INACTIVE: 'errors.subscriptionInactive',
  VALIDATION_ERROR: 'errors.validation',
  RESOURCE_NOT_FOUND: 'errors.notFound',
  DUPLICATE_RESOURCE: 'errors.duplicate',
  FORBIDDEN: 'errors.forbidden',
  TENANT_VIOLATION: 'errors.tenant',
  UNAUTHORIZED: 'errors.unauthorized',
  INVALID_TOKEN: 'errors.invalidToken',
  BUSINESS_ERROR: 'errors.business',
  INTERNAL_ERROR: 'errors.internal',
};

function hintForCode(code: string): string {
  const key = CODE_HINT_KEYS[code];
  if (!key) {
    return i18n.t('errors.internal', { ns: 'errors', defaultValue: 'Something went wrong' });
  }
  const fallbacks: Record<string, string> = {
    PLAN_LIMIT_EXCEEDED: 'Your subscription plan limit has been reached.',
    SUBSCRIPTION_INACTIVE: 'Your subscription is inactive.',
    VALIDATION_ERROR: 'Please check the highlighted fields and try again.',
    RESOURCE_NOT_FOUND: 'The item you requested could not be found.',
    DUPLICATE_RESOURCE: 'This record already exists.',
    FORBIDDEN: 'You do not have permission to perform this action.',
    TENANT_VIOLATION: 'You cannot access data from another organization.',
    UNAUTHORIZED: 'Please sign in again to continue.',
    INVALID_TOKEN: 'Your session expired. Please sign in again.',
    BUSINESS_ERROR: 'This action is not allowed in the current state.',
    INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
  };
  return i18n.t(key, { ns: 'errors', defaultValue: fallbacks[code] ?? fallbacks.INTERNAL_ERROR });
}

function humanizeField(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatValidationErrors(error: unknown): string | null {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
  const record = error as Record<string, unknown>;

  if (record.fieldErrors && typeof record.fieldErrors === 'object' && !Array.isArray(record.fieldErrors)) {
    const fieldEntries = Object.entries(record.fieldErrors as Record<string, unknown>).filter(
      ([, v]) => v != null && String(v).trim() !== '',
    );
    if (fieldEntries.length > 0) {
      return fieldEntries
        .map(([field, value]) => `${humanizeField(field)}: ${String(value)}`)
        .join(' · ');
    }
  }

  // Structured API/business errors — not field validation maps
  if (typeof record.messageKey === 'string' || typeof record.message === 'string') {
    return null;
  }

  const skipFields = new Set(['code', 'message', 'messageKey', 'messageArgs', 'fieldErrors']);
  const entries = Object.entries(record).filter(
    ([field, value]) => !skipFields.has(field) && value != null && String(value).trim() !== '',
  );
  if (entries.length === 0) return null;
  return entries
    .map(([field, value]) => `${humanizeField(field)}: ${String(value)}`)
    .join(' · ');
}

function translateMessageKey(messageKey: string, fallbackMessage: string | undefined, messageArgs: unknown): string {
  const ns = messageKey.includes('.') ? messageKey.split('.')[0] : undefined;
  const key = ns ? messageKey.slice(ns.length + 1) : messageKey;
  const replace = Array.isArray(messageArgs)
    ? (messageArgs as unknown[]).map((arg) => String(arg))
    : undefined;
  return i18n.t(key, {
    ns: ns && i18n.hasResourceBundle(i18n.language, ns) ? ns : undefined,
    defaultValue: fallbackMessage?.trim() || messageKey,
    ...(replace ? { replace } : {}),
  });
}

function extractMessageKey(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return undefined;
  const messageKey = (error as Record<string, unknown>).messageKey;
  return typeof messageKey === 'string' ? messageKey : undefined;
}

function extractCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return undefined;
  const code = (error as Record<string, unknown>).code;
  return typeof code === 'string' ? code : undefined;
}

function messageFromApiBody(body: ApiResponse<unknown> | undefined, status?: number): string | null {
  if (!body) return null;

  if (typeof body.error === 'string' && body.error.trim()) return body.error.trim();

  const messageKey = body.error && typeof body.error === 'object' && !Array.isArray(body.error)
    ? (body.error as Record<string, unknown>).messageKey
    : undefined;
  const messageArgs = body.error && typeof body.error === 'object' && !Array.isArray(body.error)
    ? (body.error as Record<string, unknown>).messageArgs
    : undefined;

  if (typeof messageKey === 'string' && messageKey.trim()) {
    return translateMessageKey(
      messageKey,
      typeof body.message === 'string' ? body.message : undefined,
      messageArgs,
    );
  }

  const rawMessage = body.message?.trim();
  if (rawMessage?.includes('Feature not enabled:')) {
    return rawMessage;
  }

  if (rawMessage && rawMessage !== 'Success') {
    const generic = ['Validation failed', 'Business rule violation', 'Resource not found', 'Internal server error'];
    const code = extractCode(body.error);
    if (!generic.includes(rawMessage) || !code) {
      return rawMessage;
    }
  }

  const validation = formatValidationErrors(body.error);
  if (validation) return validation;

  if (status === 402) return hintForCode('PLAN_LIMIT_EXCEEDED');
  if (status === 403) return hintForCode('FORBIDDEN');
  if (status === 404) return hintForCode('RESOURCE_NOT_FOUND');

  return body.message?.trim() || null;
}

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly messageKey?: string;
  readonly apiMessage: string;

  constructor(
    message: string,
    options?: { status?: number; code?: string; messageKey?: string; cause?: unknown },
  ) {
    super(message);
    this.name = 'ApiError';
    this.apiMessage = message;
    this.status = options?.status;
    this.code = options?.code;
    this.messageKey = options?.messageKey;
    if (options?.cause) this.cause = options.cause;
  }

  static fromResponse(body: ApiResponse<unknown>, status?: number): ApiError {
    const message =
      messageFromApiBody(body, status) ?? 'Request failed. Please try again.';
    return new ApiError(message, {
      status,
      code: extractCode(body.error),
      messageKey: extractMessageKey(body.error),
    });
  }

  static fromUnknown(err: unknown, fallback = 'Something went wrong. Please try again.'): ApiError {
    if (err instanceof ApiError) return err;

    if (axios.isAxiosError(err)) {
      const ax = err as AxiosError<ApiResponse<unknown>>;
      const body = ax.response?.data;
      const status = ax.response?.status;
      if (body) return ApiError.fromResponse(body, status);
      if (ax.code === 'ECONNABORTED') {
        return new ApiError('Request timed out. Please try again.', { status, cause: err });
      }
      if (!ax.response) {
        return new ApiError(
          'Cannot reach the server. Make sure the backend is running and your network is connected.',
          { cause: err },
        );
      }
      if (status === 401) return new ApiError(hintForCode('UNAUTHORIZED'), { status, cause: err });
      if (status === 403) return new ApiError(hintForCode('FORBIDDEN'), { status, cause: err });
      if (status === 404) return new ApiError(hintForCode('RESOURCE_NOT_FOUND'), { status, cause: err });
      if (status === 402) return new ApiError(hintForCode('PLAN_LIMIT_EXCEEDED'), { status, cause: err });
      if (status && status >= 500) {
        return new ApiError(hintForCode('INTERNAL_ERROR'), { status, cause: err });
      }
    }

    if (err instanceof Error && err.message.trim()) {
      return new ApiError(err.message, { cause: err });
    }

    return new ApiError(fallback, { cause: err });
  }
}

/** User-facing message from any API or thrown error. */
export function getApiErrorMessage(err: unknown, fallback?: string): string {
  return ApiError.fromUnknown(err, fallback).message;
}

export function getApiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status;
  if (axios.isAxiosError(err)) return err.response?.status;
  return undefined;
}

/** Show a toast with the best available backend error message. */
export interface ShowApiErrorOptions {
  socialAccountId?: number;
  platform?: string;
}

export function showApiError(err: unknown, fallback?: string, options?: ShowApiErrorOptions): void {
  const status = getApiErrorStatus(err);
  if (shouldSuppressAuthError(status)) {
    return;
  }
  if (status === 403 && consumePermissionDeniedHandled()) {
    return;
  }
  const apiErr = ApiError.fromUnknown(err, fallback);
  if (isSocialReconnectRequired(apiErr)) {
    void import('@/modules/social/shared/socialNotify').then(({ notifySocialErrorIfReconnect }) => {
      notifySocialErrorIfReconnect(apiErr, apiErr.message, {
        socialAccountId: options?.socialAccountId,
        platform: options?.platform,
      });
    });
    return;
  }
  notify.error(apiErr.message);
}
